import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { BaseSiteService, CmsService, GlobalMessageService, GlobalMessageType, LanguageService, RoutingService } from '@spartacus/core';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { DataLayerService } from 'src/app/shared/services/data-layer.service';
import { RecaptchaService } from 'src/app/shared/services/recaptcha.service';
import { SharedService } from 'src/app/shared/services/shared.service';
import { NumberEight, NumberThree } from '../../../shared/utils/constants/global.constants';
import { CustomRegistrationService } from '../../custom-registration.service';
import { globalErrorMsgEng, globalErrorMsgFr, siteLanguagesEng } from '../../registration.constants';

const passwordMatcher: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null => {
  const password = control.get('currentPassword');
  const confirmPassword = control.get('confirmPassword');
  return password && confirmPassword && password.value !== confirmPassword.value
    ? { passwordsDoNotMatch: true }
    : null;
};

const emailMatcher: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null => {
  const email = control.get('email');
  const confirmEmail = control.get('confirmEmail');
  return email && confirmEmail && email.value !== confirmEmail.value
    ? { emailsDoNotMatch: true }
    : null;
};

declare const grecaptcha: any;

declare global {
  interface Window {
    onRecaptchaLoad: () => void;
  }
}

interface loggedInUserData {
  accountNumber: any,
  postalCode: any
}
@Component({
    selector: 'app-account-information',
    templateUrl: './account-information.component.html',
    styleUrls: ['./account-information.component.scss'],
    standalone: false
})
export class AccountInformationComponent implements OnInit, OnDestroy, AfterViewInit {
  accountInfoForm: FormGroup;
  widgetId: any;
  token: any;
  errorMsg = false;
  header: any;
  creditInvoice: string;
  errorMessageText: string = globalErrorMsgEng;
  accountInfoSub: Subscription;
  isAccountNoValidated = false;
  isPasswordValid = true;
  errorMessageInvalidAccountNo =
    'Please be sure you have entered the correct customer number and zip code. They do not match our records. Please try again or phone 1-800-VACCINE (1-800-822-2463) for assistance.';
  vaxErrorMessageInvalidAccountNo =
    'Please be sure you have entered the correct customer number and zip code. They do not match our records. Please try again or phone 1-800-752-9338 for assistance.';
  baseSiteId = '';
  somethingwrongMessage = 'Something went wrong...! Please try again later.';
  emailAlreadyRegisteredMsg = 'Email address already registered';
  isUserEmailExistsBoth = false;
  isUserEmailExistsOne = false;
  isForgotPwdClicked = false;
  resetPwdCode = '';
  finalNeedAss: any;
  isForgotPwdLink = false;
  userUUID = '';
  isExistsPasswordInvalid = false;
  existUserInfo: any;
  vshBlockedAccountNoMessage = 'We are currently not able to accept your on-line registration.Please call 1-800-VACCINE(1-800-822-2463) to complete your order.';
  vaxBlockedAccountNoMessage = 'We are currently not able to accept your on-line registration.Please call 1-800-752-9338 to complete your order.';
  emailRegx = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,15}$/;
  recaptchaEnabled = false;
  siteKey: string | null = null;
  subRecaptcha: Subscription;
  showCaptcha = true;
  showCaptchaErrorMsg = false;
  loggedInUserData: loggedInUserData;
  accountFormValue: any;
  isloggedIn: any;

  constructor(
    private readonly accountRegService: CustomRegistrationService,
    private readonly cdr: ChangeDetectorRef,
    private readonly languageService: LanguageService,
    private readonly globalMessage: GlobalMessageService,
    private baseSite: BaseSiteService,
    private readonly routingService: RoutingService,
    private readonly sharedService: SharedService,
    private readonly cmsData: CmsService,
    private _dataLayerService: DataLayerService,
    private recaptchaService: RecaptchaService,
    private readonly user: UserAccountFacade,
    private router: Router
  ) { }

  ngAfterViewInit(): void {
    if (window['grecaptcha'] && window['grecaptcha'].enterprise) {
      this.loadRecaptcha();
    }
  }

  ngOnInit(): void {
    this.creditInvoice = 'invoiceTable';
    window.scroll(0, 0);
    const minlengthChar = 8;
    const maxlengthChar = 10;
    this.languageService.getActive().subscribe((langRes) => {
      this.errorMessageText =
        langRes === siteLanguagesEng ? globalErrorMsgEng : globalErrorMsgFr;
    });
    this.header = 'accountInformation';
    this.finalNeedAss = this.cmsData.getComponentData('NeedAssistanceComponent');

    this.accountInfoForm = new FormGroup({
      accountNumber: new FormControl('', [
        Validators.required,
        Validators.minLength(minlengthChar),
        Validators.maxLength(maxlengthChar),
      ]),
      postalCode: new FormControl('', [
        Validators.required,
        Validators.pattern('^[0-9]{5}$|^[0-9]{5}-[0-9]{4}$'),
      ]),
      email: new FormControl('', [
        Validators.required,
        Validators.email,
        Validators.pattern(this.emailRegx),
      ]),
      captchaToken: new FormControl('', Validators.required),
    },
      {
        validators: [passwordMatcher, emailMatcher],
      });

    this.accountInfoForm.get('postalCode').valueChanges.subscribe((value) => {
      let str = this.accountInfoForm.get('postalCode').value;
      if (str.charAt(str.length - 1) === '-')
        this.accountInfoForm
          .get('postalCode')
          .setValue(str.substring(0, str.length - 1));
    });

    this.accountInfoForm.valueChanges.subscribe((value) => { });
    this.baseSite
      .getActive()
      .pipe(take(1))
      .subscribe((res) => (this.baseSiteId = res));

    this.routingService.getRouterState().subscribe(res => {
      if (res && res.state.queryParams.code) {
        this.resetPwdCode = res.state.queryParams.code;
        this.isForgotPwdLink = true;
        this.accountInfoForm.removeControl('currentPassword');
        this.accountInfoForm.addControl(
          'currentPassword',
          new FormControl('', [
            Validators.required,
            this.validatePassword.bind(this),
          ])
        );
        this.accountInfoForm.addControl(
          'confirmPassword',
          new FormControl('', [
            Validators.required,
            this.validatePassword.bind(this),
          ])
        );

      }
    });
    this._dataLayerService.addtoDataLayer('userReg_FormEvent', '', '', '', 'form_start', 'account');
    this._dataLayerService.addtoDataLayer('userReg_FormEvent', '', '', '', 'form_step', 'account');

    this.subRecaptcha = this.recaptchaService.isEnabled$().subscribe((enabled) => {
      this.recaptchaEnabled = enabled;
      this.siteKey = this.recaptchaService.getSiteKey();
      if (!enabled) {
        this.accountInfoForm.get('captchaToken')?.clearValidators();
        this.accountInfoForm.get('captchaToken')?.updateValueAndValidity();
      }
    });
    this.userDetection();
    window['onRecaptchaLoad'] = () => this.loadRecaptcha();
  }

  private loadRecaptcha() {
    const container = document.getElementById('recaptcha-container');
    if (!container || !window['grecaptcha'] || !grecaptcha.enterprise.render) {
      console.error('Recaptcha not ready');
      return;
    }

    this.widgetId = grecaptcha.enterprise.render('recaptcha-container', {
      sitekey: this.siteKey,
      theme: 'light',
      size: 'normal',
      callback: (token: string) => this.onCaptchaSuccess(token)
    });
  }

  private onCaptchaSuccess(token: string) {
    //console.log('Captcha success token:', token);
    this.accountInfoForm.patchValue({ captchaToken: token });
  }

  validateAccountInfo() {
    console.log(this.accountInfoForm.controls.captchaToken?.errors);
    if (this.accountInfoForm.contains('captchaToken')) this.showCaptchaErrorMsg = this.accountInfoForm.controls.captchaToken?.errors?.required && true;

    this.accountInfoForm.markAllAsTouched();
    if (!this.isAccountNoValidated) {
      if (this.accountInfoForm.valid) {
        this.reinitializeFormData()
        this.accountInfoSub = this.accountRegService
          .postValidateAccountInfo(this.accountFormValue)
          .subscribe(
            (res) => {
              this.showCaptcha = false;
              this.isUserEmailExistsOne = res.existingUserCheck;
              this.addFormControl(res.existingUserCheck);
              this.errorMsg = false;
              this.isAccountNoValidated = true;
              this.isUserEmailExistsBoth = false;
              this.userUUID = res.uuid;
              if (res.uuid)
                this.sharedService.setLocalStorage('userUUID', this.userUUID);
              this.sharedService.setLocalStorage('existingUserCheck', res.existingUserCheck);
              this.existUserInfo = res;
              this.cdr.detectChanges();
            },
            (error) => {
              if (!!error) {
                this.isAccountNoValidated = false;
                this.errorMsg = true;
                window.scroll(0, 0);
                if (error.error.errors[0].message.includes('accountNumber') || error.error.errors[0].message.includes('postalCode')) {
                  this.globalMessage.remove(GlobalMessageType.MSG_TYPE_ERROR);
                  if (this.baseSiteId === 'vsh') {
                    this.globalMessage.add(
                      this.errorMessageInvalidAccountNo,
                      GlobalMessageType.MSG_TYPE_ERROR
                    );
                  } else {
                    this.globalMessage.add(
                      this.vaxErrorMessageInvalidAccountNo,
                      GlobalMessageType.MSG_TYPE_ERROR
                    );
                  }

                }
                else if (error.error.errors[0].message.includes('accountNumber') || error.error.errors[0].message.includes('secure site')) {
                  this.globalMessage.remove(GlobalMessageType.MSG_TYPE_ERROR);
                  if (this.baseSiteId === 'vsh') {
                    this.globalMessage.add(
                      this.vshBlockedAccountNoMessage,
                      GlobalMessageType.MSG_TYPE_ERROR
                    );
                  } else {
                    this.globalMessage.add(
                      this.vaxBlockedAccountNoMessage,
                      GlobalMessageType.MSG_TYPE_ERROR
                    );
                  }
                }
                else if (error.error.errors[0].message.includes('email')) {
                  this.globalMessage.add(
                    this.emailAlreadyRegisteredMsg,
                    GlobalMessageType.MSG_TYPE_ERROR
                  );
                }
                else {
                  this.globalMessage.add(
                    this.somethingwrongMessage,
                    GlobalMessageType.MSG_TYPE_ERROR
                  );

                }
                this.cdr.detectChanges();
              }
              this._dataLayerService.addtoDataLayer('userReg_FormEvent', '', '', '', 'form_error', 'account');
            }
          );
      } else {
        this.showGlobalMessage();
        this._dataLayerService.addtoDataLayer('userReg_FormEvent', '', '', '', 'form_error', 'user');
      }
    } else {
      if (this.isUserEmailExistsOne == false) {
        this.reinitializeFormData(true);
        this.accountInfoForm.markAllAsTouched();
        if (this.accountInfoForm.valid) {
          const accountInfo = {
            header: 'userInformation',
            form: this.accountInfoForm,
            isLoggedIn: !!this.isloggedIn
          };
          this.accountRegService.sectionChange.next(accountInfo);
        } else {
          this.showGlobalMessage();
        }
        this.isUserEmailExistsBoth = false;
      }
      else {
        if (this.accountInfoForm.get('currentPassword').value !== '') {
          const param = {
            email: this.accountInfoForm.get('email').value,
            password: this.accountInfoForm.get('currentPassword').value
          }
          this.accountRegService.checkExistingPassword(param).subscribe((res) => {
          },
            (error) => {
              if (!!error) {
                if (error && error?.status && error.status != '200') {
                  this.isExistsPasswordInvalid = true;
                  this.accountInfoForm.controls['currentPassword'].setErrors({ 'incorrect': true })
                  this.cdr.detectChanges();
                  this.showGlobalMessage();
                  return
                } else {
                  this.reinitializeFormData(true);
                  const accountInfo = {
                    header: 'userInformation',
                    form: this.accountInfoForm,
                    isLoggedIn: !!this.isloggedIn
                  };
                  this.accountRegService.sectionChange.next(accountInfo);
                  const obj = {
                    userName: this.existUserInfo.userName,
                    uuid: '',
                    firstName: this.existUserInfo.firstName,
                    lastName: this.existUserInfo.lastName,
                    code: ''
                  }
                  this.accountRegService.existUserInfo.next(obj);
                }
              }
            })
        }
      }
    }
  }

  validatePassword(password: FormControl): { [s: string]: boolean } {

    let counter = 0;
    let lengthCounterFlag = false;
    let nonSpecialCounterFlag = false;
    let specialCounterFlag = false;
    const str = password.value;
    const allowedSpecialCharRegex = new RegExp('^(?=.*?[*@$%!()&^]).{1,15}$');
    const allSymbolRegex = /[ `#_+\-=\[\]{};':"\\|,.<>\/?~]/;

    if (/[a-z]/.test(str)) {

      counter++;
    }
    if (/[A-Z]/.test(str)) {
      counter++;
    }
    if (/[0-9]/.test(str)) {
      counter++;
    }
    if (/[^a-zA-Z0-9]/.test(str)) {
      nonSpecialCounterFlag = true;
    }

    if (str.length >= NumberEight) {
      lengthCounterFlag = true;
    }

    const specialChars = this.getAllSpecialChars(str);

    if (nonSpecialCounterFlag && /^[*@$#%!)(&^-]+$/.test(specialChars)) {
      specialCounterFlag = true;
    }
    if (allowedSpecialCharRegex.test(str) === true) {
      counter++;
    }

    if (allSymbolRegex.test(str) === true) {
      counter = 0;

    }

    this.checkAllRulesForPassword(
      counter,
      lengthCounterFlag,
      nonSpecialCounterFlag,
      specialCounterFlag
    );

    if (!this.isPasswordValid) {
      return { isPasswordInvalid: true };
    } else {
      return null;
    }
  }

  checkAllRulesForPassword(
    counter,
    lengthCounterFlag,
    nonSpecialCounterFlag,
    specialCounterFlag
  ) {
    if (
      (counter >= NumberThree &&
        lengthCounterFlag &&
        !nonSpecialCounterFlag) ||
      (counter >= NumberThree &&
        lengthCounterFlag &&
        nonSpecialCounterFlag &&
        specialCounterFlag)
    ) {
      this.isPasswordValid = true;
    } else {
      this.isPasswordValid = false;
    }
  }

  getAllSpecialChars(str: string): string {
    const strAr = str.split('');
    const noSpecialChars = '';
    const resArr = [];
    for (const strItem of strAr) {
      if (/[^a-zA-Z0-9]/.test(strItem)) {
        resArr.push(strItem);
      }
    }
    if (resArr.length > 0) {
      return resArr.join('');
    } else {
      return noSpecialChars;
    }
  }

  numberValidation(event): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }

  addFormControl(existingUserCheck) {
    if (!existingUserCheck) {
      this.accountInfoForm.addControl(
        'confirmEmail',
        new FormControl('', [
          Validators.required,
          Validators.email,
          Validators.pattern(this.emailRegx),
        ])
      );
    } else {
      this.accountInfoForm.addControl(
        'currentPassword',
        new FormControl('', [
          Validators.required,
          this.validatePassword.bind(this),
        ])
      );
    }
    this.accountInfoForm.get('accountNumber').disable();
    this.accountInfoForm.get('postalCode').disable();
    this.accountInfoForm.get('email').disable();
  }

  showGlobalMessage() {
    window.scroll(0, 0);
    this.globalMessage.add(
      this.errorMessageText,
      GlobalMessageType.MSG_TYPE_ERROR
    );
  }

  ngOnDestroy() {
    this.accountInfoSub?.unsubscribe();
    this.subRecaptcha?.unsubscribe();
  }

  navigateForgotPassword() {
    const param = {
      email: this.accountInfoForm.get('email').value,
      accountNumber: this.accountInfoForm.get('accountNumber').value,
      postalCode: this.accountInfoForm.get('postalCode').value,
      uuid: this.userUUID
    }
    this.accountRegService.forgotPasswordLink(param).subscribe((res) => { })
    this.isForgotPwdClicked = true;
    window.scroll(0, 0);
    this.sharedService.setLocalStorage('userEmail', this.accountInfoForm.get('email').value);
    this.sharedService.setLocalStorage('userUUID', this.userUUID);
  }

  resetPassword() {
    const param = {
      email: this.sharedService.getLocalStorage('userEmail'),
      uuid: this.sharedService.getLocalStorage('userUUID'),
      code: this.resetPwdCode,
      password: this.accountInfoForm.get('currentPassword').value,
      confirmPassword: this.accountInfoForm.get('confirmPassword').value,
    }
    this.accountRegService.resetPassword(param).subscribe((res) => {
      if (res) {
        const obj = {
          userName: res.userName,
          uuid: res.uuid,
          firstName: res.firstName,
          lastName: res.lastName,
          code: this.resetPwdCode
        }
        this.accountRegService.existUserInfo.next(obj);
        this.accountInfoForm.get('accountNumber').setValue(res.accountNumber);
        this.accountInfoForm.get('postalCode').setValue(res.postalCode);
        this.accountInfoForm.get('email').setValue(this.sharedService.getLocalStorage('userEmail'));
        const accountInfo = {
          header: 'userInformation',
          form: this.accountInfoForm,
        };
        this.accountRegService.sectionChange.next(accountInfo);
      }
    })
  }

  userDetection() {
    this.user.get().pipe(take(2), map(user => !!user ? this.confirmUserData(user) : this.redirectUser())).subscribe();
  }

  confirmUserData(user) {
    const accountNumber = localStorage.getItem('accountId').replace(/^"|"$/g, '')
    if (user) this.accountRegService.getLoggedinAccountPostalCode(user.uid, accountNumber).pipe(take(1), map(acct => this.prepopulateFields(acct, accountNumber))).subscribe();
  }

  redirectUser() {
    if (this.router.url.includes("/my-account")) this.router.navigateByUrl('/');
  }

  prepopulateFields(acct, accountNumber) {
    this.isloggedIn = true;
    this.loggedInUserData = { accountNumber: accountNumber, postalCode: acct.postalCode }
    this.accountInfoForm.controls.accountNumber.setValue(accountNumber);
    this.accountInfoForm.controls.postalCode.setValue(acct.postalCode);
    this.accountInfoForm.controls.accountNumber.disable();
    this.accountInfoForm.controls.postalCode.disable();
  }

  reinitializeFormData(shiftPage?) {
    if (!!this.loggedInUserData) {
      this.accountFormValue = this.accountInfoForm.getRawValue();
      for (let key in this.loggedInUserData) {
        if (this.accountFormValue.hasOwnProperty(key) && this.accountFormValue[key] !== this.loggedInUserData[key]) {
          this.accountFormValue[key] = this.loggedInUserData[key];
          this.accountInfoForm.controls[key].patchValue(this.loggedInUserData[key]);
        }
      }
    } else {
      this.accountFormValue = this.accountInfoForm.value
    }
    if (shiftPage) {
      this.accountInfoForm.controls.accountNumber.enable();
      this.accountInfoForm.controls.postalCode.enable();
    }

  }
}